import BlankOrder from '@components/blank/BlankOrder';
import Column from '@components/Column';
import Header from '@components/Header';
import InfinityScrollTrigger from '@components/InfinityScrollTrigger';
import TabItem from '@components/tab/TabItem';
import TabList from '@components/tab/TabList';
import styled from '@emotion/styled';
import { AppScreen } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType, useStepFlow } from '@stackflow/react';
import { useTranslation } from 'react-i18next';

import OrderLog from './components/OrderLog';
import OrderLogSkeletons from './components/OrderLogSkeletons';
import useOrderLogs from './hooks/useOrderLogs';

interface Params {
    duration: 'WEEK'| 'ONE_MONTH' | 'THREE_MONTH' | 'ALL'
}

declare module '@stackflow/config' {
    interface Register {
        OrderLogsActivity: Params;
    }
}

const OrderLogsActivity: ActivityComponentType<'OrderLogsActivity'> = ({ params }) => {
    const { duration } = params;
    const { t } = useTranslation('orderLogs');
    const { replaceStep } = useStepFlow('OrderLogsActivity');
    const {
        orderLogs, fetchNextPage, isEmpty, isFetching,
    } = useOrderLogs(duration);
    return (
        <AppScreen>
            <Header title={t('header.title')} />
            <TabList variant='solid'>
                <TabItem selected={duration === 'WEEK'}
                    onClick={() => replaceStep({ duration: 'WEEK' })} >
                    {t('tabs.oneWeek')}
                </TabItem>
                <TabItem selected={duration === 'ONE_MONTH'}
                    onClick={() => replaceStep({ duration: 'ONE_MONTH' })} >
                    {t('tabs.oneMonth')}
                </TabItem>
                <TabItem selected={duration === 'THREE_MONTH'}
                    onClick={() => replaceStep({ duration: 'THREE_MONTH' })} >
                    {t('tabs.threeMonths')}
                </TabItem>
                <TabItem selected={duration === 'ALL'}
                    onClick={() => replaceStep({ duration: 'ALL' })} >
                    {t('tabs.sixMonths')}
                </TabItem>
            </TabList>
            <Contents as='main'
                fullWidth
                align='center' >
                {orderLogs?.map((order) => (
                    <OrderLog order={order}
                        key={order.orderNo} />
                ))}
                {(isEmpty && !isFetching) && (
                    <BlankOrder/>
                )}
                {isFetching && (
                    <OrderLogSkeletons count={orderLogs?.length ? 1 : 3}/>
                )}
                <InfinityScrollTrigger trigger={fetchNextPage}/>
            </Contents>
        </AppScreen>
    );
};

const Contents = styled(Column)`
    padding: var(--spacing-5);
    row-gap: 18px;
`;

export default OrderLogsActivity;
